import * as React from "react";
import { CheckCircle, XCircle, Clock, MinusCircle } from "react-feather";
import { Agent, AgentInfo } from "../../../schema/types/agent/entity";
import { agentHelpers } from "../../../mst/kinds/agent.helpers";
import { HEALTH_COLORS } from "../utils";
import { InternalLink } from "../../../newcomponents/table/components/InternalLink";
import { linksOf } from "../../../services/cpln";

export type AgentStatus = "active" | "recently-inactive" | "never-active" | "checking";

export function getAgentStatus(agent: Agent, info: AgentInfo[] | undefined): AgentStatus {
  // Check if still loading first
  const lastActiveText = agentHelpers.getLastActiveText(agent, info);
  if (lastActiveText === "Checking") return "checking";

  const isActive = agentHelpers.getIsActive(agent, info);
  if (isActive) return "active";

  if (lastActiveText.includes("never")) return "never-active";

  return "recently-inactive";
}

interface AgentHealthCardProps {
  agent: Agent;
  info: AgentInfo[] | undefined;
}

export const AgentHealthCard: React.FC<AgentHealthCardProps> = ({ agent, info }) => {
  const lastActiveText = agentHelpers.getLastActiveText(agent, info);
  const selfLink = linksOf(agent).self;
  const status = getAgentStatus(agent, info);

  const getStatusIcon = () => {
    switch (status) {
      case "active":
        return <CheckCircle size={16} color={HEALTH_COLORS.ready} />;
      case "recently-inactive":
        return <XCircle size={16} color={HEALTH_COLORS.error} />;
      case "never-active":
        return <MinusCircle size={16} color={HEALTH_COLORS.inactive} />;
      case "checking":
        return <Clock size={16} color={HEALTH_COLORS.inactive} />;
    }
  };

  const getStatusLabel = () => {
    switch (status) {
      case "active":
        return "Active";
      case "recently-inactive":
        return "Recently Inactive";
      case "never-active":
        return "Never Active";
      case "checking":
        return "Checking";
    }
  };

  const getStatusColors = () => {
    switch (status) {
      case "active":
        return { bg: "rgba(16, 185, 129, 0.1)", color: HEALTH_COLORS.ready };
      case "recently-inactive":
        return { bg: "rgba(239, 68, 68, 0.1)", color: HEALTH_COLORS.error };
      case "never-active":
        return { bg: "rgba(107, 114, 128, 0.1)", color: HEALTH_COLORS.inactive };
      case "checking":
        return { bg: "rgba(107, 114, 128, 0.1)", color: HEALTH_COLORS.inactive };
    }
  };

  const statusColors = getStatusColors();

  return (
    <div
      className="rounded-lg p-4 overflow-hidden"
      style={{
        backgroundColor: "var(--health-card-bg)",
        border: "1px solid var(--color-border)",
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <div className="flex-shrink-0">{getStatusIcon()}</div>
        <div className="truncate min-w-0 flex-1 text-sm font-medium">
          {selfLink ? (
            <InternalLink label={agent.name} to={`/console${selfLink}`} />
          ) : (
            <span style={{ color: "var(--color-default)" }}>{agent.name}</span>
          )}
        </div>
      </div>

      {/* Status */}
      <div className="flex items-center gap-1 text-xs" style={{ color: "var(--color-label)" }}>
        <Clock size={12} />
        <span>{lastActiveText}</span>
      </div>

      {/* Status badge */}
      <div className="mt-2">
        <span
          className="text-xs px-2 py-0.5 rounded-full"
          style={{
            backgroundColor: statusColors.bg,
            color: statusColors.color,
          }}
        >
          {getStatusLabel()}
        </span>
      </div>
    </div>
  );
};
